import { Locator, LocatorLocations, LocatorText } from '@readium/shared';
import type { LocatorLike } from '../models/metadata-response';
import type { QuizMaterialResponse } from '../models/quiz';
import type { Highlight } from '../models/selections';
import { isLegacyCfi } from './cfi';
import {
  CHAPTER_START_PROGRESSION,
  type BookChapters,
  findComponentPositions,
  normalizeProgression,
} from './locator';
import { storedProgression } from './quiz-placement';

function usableQuote(
  text: { before?: string; highlight?: string; after?: string } | undefined,
): LocatorText | undefined {
  const highlight = text?.highlight?.trim();
  return highlight
    ? new LocatorText({ before: text?.before, highlight, after: text?.after })
    : undefined;
}

// The chapter position the question's progression falls on: the one with the largest progression
// not past it, or the chapter's first. It lets the payment pre-check, which reads the progress from
// `locations.position`, see roughly how far into the book the question is.
function positionAt(chapter: LocatorLike[], progression: number): LocatorLike {
  let best: LocatorLike | undefined = undefined;
  for (const entry of chapter) {
    const own = entry.locations.progression;
    if (own === undefined || own > progression) continue;
    if (!best || own > (best.locations.progression ?? -1)) best = entry;
  }
  return best ?? chapter[0];
}

/**
 * Where to navigate to show a question, mirroring how isQuizOnPage places it: Readium first looks
 * for its own text anchor or, failing that, its Highlight's text (go_text), then goes to the
 * stored progression — the chapter's last page without a usable position, its first page for a
 * legacy CFI, which a locator cannot express.
 */
export function quizLocator(
  quiz: QuizMaterialResponse,
  highlights: Highlight[],
  { positions, componentNames }: BookChapters,
): Locator | undefined {
  const { selection } = quiz;
  const highlight = highlights.find(({ id }) => id === selection.id);
  const chapter = findComponentPositions(selection.componentId, positions, componentNames);
  // A question whose component is not in the book cannot be placed on any page, so going to its
  // highlight (when it has one) is only a best effort.
  if (!chapter.length) return highlight && Locator.deserialize(highlight.locator);

  // A legacy CFI cannot be expressed as a locator: open the chapter at its start (reading order)
  // and let the placement resolve the CFI once the chapter is loaded.
  const percent = selection.percent ?? '';
  const progression = isLegacyCfi(percent) ? 0 : normalizeProgression(storedProgression(percent));
  const position = positionAt(chapter, progression);
  return new Locator({
    href: chapter[0].href,
    type: chapter[0].type,
    locations: new LocatorLocations({
      position: position.locations.position,
      progression: Math.max(CHAPTER_START_PROGRESSION, progression),
    }),
    text: usableQuote(selection.textAnchor) ?? usableQuote(highlight?.locator.text),
  });
}
